import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import axios from 'axios';

const API_URL = '/api';

axios.defaults.baseURL = API_URL;

// Token Helper Functions
const getTokenExpiry = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const decoded = JSON.parse(jsonPayload);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (e) {
    return null;
  }
};

const isTokenExpiringSoon = (token, minutesThreshold = 30) => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  
  const now = Date.now();
  const timeLeft = expiry - now;
  const thresholdMs = minutesThreshold * 60 * 1000;
  
  return timeLeft < thresholdMs;
};

const getMinutesLeft = (token) => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  
  const now = Date.now();
  const timeLeft = expiry - now;
  return Math.floor(timeLeft / (60 * 1000));
};

// Axios Interceptor Component
function AxiosInterceptor({ onLogout, onTokenRefreshNeeded }) {
  const navigate = useNavigate();
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error, token = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    failedQueue.current = [];
  };

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem('token');
        
        // Add token to request headers
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Check if token is expiring soon before each request
        if (token && isTokenExpiringSoon(token, 5)) {
          console.log('Token läuft in weniger als 5 Minuten ab - automatische Erneuerung...');
          
          if (!isRefreshing.current) {
            try {
              await onTokenRefreshNeeded();
            } catch (error) {
              console.error('Automatische Token-Erneuerung fehlgeschlagen:', error);
            }
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing.current) {
            // Already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axios(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            // Try to refresh token
            await onTokenRefreshNeeded();
            
            const newToken = localStorage.getItem('token');
            if (newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              processQueue(null, newToken);
              isRefreshing.current = false;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed - logout
            processQueue(refreshError, null);
            isRefreshing.current = false;
            console.log('Token-Erneuerung fehlgeschlagen - Logout');
            onLogout();
            navigate('/login');
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate, onLogout, onTokenRefreshNeeded]);

  return null;
}

// Session Warning Modal
function SessionWarningModal({ show, onRenew, onLogout, minutesLeft }) {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '450px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px'
          }}>⏰</div>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 8px 0'
          }}>Sitzung läuft bald ab</h2>
          <p style={{ 
            fontSize: '16px',
            color: '#666',
            margin: 0
          }}>
            Ihre Sitzung läuft in {minutesLeft} Minuten ab.
            {minutesLeft <= 5 && ' Bitte speichern Sie Ihre Arbeit!'}
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          flexDirection: 'column'
        }}>
          <button
            onClick={onRenew}
            style={{
              padding: '14px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Sitzung verlängern
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '14px 24px',
              background: 'white',
              color: '#666',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#f5f7fa';
              e.target.style.borderColor = '#ccc';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'white';
              e.target.style.borderColor = '#e0e0e0';
            }}
          >
            Abmelden
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [minutesLeft, setMinutesLeft] = useState(0);
  const sessionCheckInterval = useRef(null);
  const warningShownRef = useRef(false);
  const isRefreshing = useRef(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Überprüfe Token-Gültigkeit beim Start
      axios.get('/firmen')
        .catch((error) => {
          if (error.response?.status === 401) {
            console.log('Token ungültig beim Start - versuche Refresh');
            handleTokenRefresh().catch(() => {
              console.log('Refresh fehlgeschlagen - Logout');
              handleLogout();
            });
          }
        });

      // Start session monitoring
      startSessionMonitoring();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      stopSessionMonitoring();
    }

    return () => {
      stopSessionMonitoring();
    };
  }, [token]);

  const startSessionMonitoring = () => {
    stopSessionMonitoring();
    warningShownRef.current = false;

    // Check every minute
    sessionCheckInterval.current = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        stopSessionMonitoring();
        return;
      }

      const minutesRemaining = getMinutesLeft(currentToken);

      // Auto-refresh at 10 minutes if possible
      if (minutesRemaining <= 10 && minutesRemaining > 5 && !isRefreshing.current) {
        console.log('Auto-refresh: Token läuft bald ab');
        handleTokenRefresh().catch((err) => {
          console.error('Auto-refresh fehlgeschlagen:', err);
        });
      }

      // Show warning at 30, 15, and 5 minutes
      if ((minutesRemaining === 30 || minutesRemaining === 15 || minutesRemaining === 5) && 
          !warningShownRef.current) {
        console.log(`Warnung: Token läuft in ${minutesRemaining} Minuten ab`);
        setMinutesLeft(minutesRemaining);
        setShowSessionWarning(true);
        warningShownRef.current = true;
        
        // Reset warning flag after showing
        setTimeout(() => {
          warningShownRef.current = false;
        }, 60000); // Reset after 1 minute
      }

      // Auto-logout if expired
      if (minutesRemaining <= 0) {
        console.log('Token abgelaufen - automatischer Logout');
        handleLogout();
      }
    }, 60000); // Check every minute
  };

  const stopSessionMonitoring = () => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  };

  const handleLogin = (data) => {
    // data should contain: access_token, refresh_token (optional)
    const accessToken = data.access_token || data;
    const refreshTokenValue = data.refresh_token || null;
    
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    
    if (refreshTokenValue) {
      localStorage.setItem('refresh_token', refreshTokenValue);
      setRefreshToken(refreshTokenValue);
    }
    
    setShowSessionWarning(false);
    warningShownRef.current = false;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setRefreshToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setShowSessionWarning(false);
    stopSessionMonitoring();
  };

  const handleTokenRefresh = async () => {
    if (isRefreshing.current) {
      console.log('Refresh bereits in Bearbeitung, überspringe...');
      return;
    }

    const storedRefreshToken = localStorage.getItem('refresh_token');
    
    if (!storedRefreshToken) {
      console.log('Kein Refresh Token vorhanden');
      throw new Error('No refresh token available');
    }

    isRefreshing.current = true;

    try {
      console.log('Versuche Token zu erneuern...');
      
      const response = await axios.post('/auth/refresh', {
        refresh_token: storedRefreshToken
      });
      
      const newAccessToken = response.data.access_token;
      
      localStorage.setItem('token', newAccessToken);
      setToken(newAccessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
      
      console.log('Token erfolgreich erneuert');
      
      // Close warning if open
      setShowSessionWarning(false);
      warningShownRef.current = false;
      
      // Show success notification
      showNotification('Sitzung wurde automatisch verlängert', 'success');
      
      return newAccessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      if (error.response?.status === 401) {
        // Refresh token is also expired
        showNotification('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.', 'error');
        handleLogout();
      }
      
      throw error;
    } finally {
      isRefreshing.current = false;
    }
  };

  const handleRenewSession = async () => {
    try {
      await handleTokenRefresh();
      setShowSessionWarning(false);
      warningShownRef.current = false;
    } catch (error) {
      console.error('Session renewal failed:', error);
      // Will be handled by handleTokenRefresh
    }
  };

  const showNotification = (message, type = 'info') => {
    const colors = {
      success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      info: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    };

    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type] || colors.info};
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10001;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
      max-width: 400px;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 5000);
  };

  return (
    <Router>
      <AxiosInterceptor onLogout={handleLogout} onTokenRefreshNeeded={handleTokenRefresh} />
      <SessionWarningModal 
        show={showSessionWarning}
        onRenew={handleRenewSession}
        onLogout={handleLogout}
        minutesLeft={minutesLeft}
      />
      <Routes>
        <Route 
          path="/login" 
          element={token ? <Navigate to="/" /> : <Login onLogin={handleLogin} />} 
        />
        <Route 
          path="/" 
          element={token ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
      </Routes>
    </Router>
  );
}

export default App;